import {
  EventFormatEnum,
  EventStatusEnum,
  NonStaffEventListRecordType,
  NonStaffEventRecordType,
} from "pages/Event/Event.types";
import {
  AttendeeRecordType,
  ContactInfoType,
  CustomFieldDataType,
  FileRecordType,
  MinimalUserRecordType,
  MinimalUserWithContactInfoRecordType,
  NonStaffFileRecordType,
  PriceRecordType,
} from "pages/SharedRecords.types";
import { Identifier } from "react-admin";

type InvoiceMetadataType = {
  event_base: number;
  event_base_name: string;
  event_id_number: string;
  event_status: EventStatusEnum;
  checkout: CheckoutRecordType | null;
};

export enum InvoiceKindEnum {
  Standard = "STANDARD",
  Correction = "CORRECTION",
  Reversal = "REVERSAL",
}

export type InvoiceRecordType = {
  attendee: number|undefined;
  booking: number;
  booking_number: string;
  children: InvoiceRecordType[];
  created_at: string;
  debit_number: string;
  event: number;
  event_unique_invoice_number: number;
  external_accounting_url: string | null;
  first_name: string;
  id: number;
  invoice_contact_info: ContactInfoType | null;
  invoice_dispatch_contact_info: ContactInfoType | null;
  invoice_number: string | null;
  is_paid: boolean;
  kind: InvoiceKindEnum;
  last_name: string;
  main_contact_info: ContactInfoType | null;
  meta: InvoiceMetadataType;
  name: string;
  paid_amount: number;
  parent: number | null;
  price: PriceRecordType | null;
  pdf: FileRecordType | null;
  sent_at: string;
  accounting_transfer_started_at: string | null;
  accounting_transfer_success_at: string | null;
  accounting_transfer_error: { [key: string]: string | boolean | number };
  service_period: string;
  template: number | null;
  title: string | null;
  document_task_id?: number | null;
};

export type AccountingInvoiceRecordType = {
  id: number;
  number: string;
  paid_amount: number | null;
};

export enum BookingStatusEnum {
  Waiting = "WAITING",
  Valid = "VALID",
  PaymentPending = "PAYMENT_PENDING",
  Canceled = "CANCELED",
  Rebooked = "REBOOKED",
}

export type CheckoutRecordType = {
  id: number;
  payment_status: "PENDING" | "COMPLETED" | "FAILURE";
  payment_provider: "STRIPE" | "EDM_INVOICE";
  checkout_id?: string;
  payment_id?: string;
};

export type BookingRecordType<AttendeeType = number, PriceType = PriceRecordType | null, EventType = number> = {
  id: number;
  status: BookingStatusEnum;
  attendee: AttendeeType;
  booking_number: string | null;
  event: EventType;
  event_id_number: string;
  enrollment: number | null;
  is_self_managed_payment: boolean;
  checkout: CheckoutRecordType;
  files: FileRecordType[];
  price: PriceType;
  created_at: string;
  invoices: InvoiceRecordType[];
  event_attendances: {
    event: number;
    booking: number;
    event_date?: number;
    has_attended: boolean | null;
  }[];
  format: string;
  rebooked_from?: number;
  rebooked_to?: number;
  archived_at?: string;
  meta: {
    attendee: AttendeeRecordType;
    event_group_children: number[];
    rebooked_from_booking_number?: string;
    rebooked_to_booking_number?: string;
    has_attended: boolean | null;
    enrollment_processing: boolean | null;
    is_billable: boolean;
  };
  custom_fields_data?: CustomFieldDataType;
};

export type LecturerBookingRecordType = {
  id: number;
  status: BookingStatusEnum;
  booking_number: string | null;
  event: Identifier;
  attendee: MinimalUserRecordType | MinimalUserWithContactInfoRecordType;
  files: NonStaffFileRecordType[];
  created_at: string;
  event_attendances: {
    id: number;
    booking: number;
    event: number;
    event_date?: number;
    has_attended: boolean;
  }[];
  meta: {
    event_group_children: Identifier[];
    organization_name: string | null;
  };
};

export type AttendeeBookingListRecordType = {
  id: number;
  status: BookingStatusEnum;
  booking_number: string | null;
  event: NonStaffEventListRecordType;
  created_at: Date;
};

export type AttendeeBookingRecordType = Omit<AttendeeBookingListRecordType, "event"> & {
  event: NonStaffEventRecordType;
  files: NonStaffFileRecordType[];
  price: PriceRecordType;
  invoices: InvoiceRecordType[];
  format: EventFormatEnum;
};
